import type { ObrasSortField } from "@/modules/obras";
import type { ObrasColumnKey } from "./types-obras-lista";

export const OBRAS_TITLE = "Painel de Obras";
export const OBRAS_TAB_DETALHAMENTO = "Detalhamento";
export const OBRAS_TAB_INDICADORES = "Indicadores";
/** Texto do carregamento (spinner) da Lista e dos Cards, lido pelos leitores de tela. */
export const OBRAS_LOADING_LABEL = "Carregando obras...";
/** HU 2521 RN02: obras sem status (Lista mostra "-"): opção do filtro e coluna final dos Cards. */
export const OBRAS_SEM_STATUS = { code: "SEM_STATUS", label: "Sem status" } as const;
/** Condição e indicadores sem fonte de dados (Impasse 14); o Figma mostra o hífen (5651:228915). */
export const OBRAS_NO_VALUE = "-";
export const OBRAS_FUTURE_FEATURE_TITLE = "Disponível em uma próxima entrega.";

export interface ObrasColumnDefinition {
  key: ObrasColumnKey;
  label: string;
  /** Rótulo abreviado exibido no cabeçalho; o completo vai na tooltip. */
  header: string;
  /** Principais: sempre visíveis (Padrões Globais). */
  main: boolean;
  /** Campo de ordenação do contrato; ausente = coluna não ordenável (Impasse 48). */
  sortField?: ObrasSortField;
  align?: "right";
  /** Tooltip com o nome completo, só nos cabeçalhos abreviados. */
  tooltip?: string;
}

export const OBRAS_COLUMNS: ObrasColumnDefinition[] = [
  { key: "id", label: "ID", header: "ID", main: true, sortField: "id" },
  {
    key: "embarcacao",
    label: "Embarcação",
    header: "Embarcação",
    main: true,
    sortField: "embarcacao",
  },
  {
    key: "statusObra",
    label: "Status da Obra",
    header: "Status da Obra",
    main: true,
    sortField: "statusObra",
  },
  { key: "tipoObra", label: "Tipos de Obra", header: "Tipos de Obra", main: true },
  { key: "condicao", label: "Condição", header: "Condição", main: false },
  { key: "inicio", label: "Início", header: "Início", main: true, sortField: "inicio" },
  { key: "termino", label: "Término", header: "Término", main: true, sortField: "termino" },
  {
    key: "duracao",
    label: "Duração Total",
    header: "Duração Total",
    main: false,
    align: "right",
    tooltip: "Duração Total",
  },
  {
    key: "realizado",
    label: "Realizado BRL M",
    header: "Realizado",
    main: false,
    align: "right",
    tooltip: "Realizado BRL M",
  },
  {
    key: "totalServicos",
    label: "Total Serviços",
    header: "Total Serviços",
    main: false,
    align: "right",
    tooltip: "Total Serviços",
  },
  {
    key: "percServicos",
    label: "% Serviços Concluídos",
    header: "% Serv. Concluídos",
    main: false,
    align: "right",
    tooltip: "% Serviços Concluídos",
  },
  {
    key: "totalMateriais",
    label: "Total Materiais",
    header: "Total Materiais",
    main: false,
    align: "right",
    tooltip: "Total Materiais",
  },
  {
    key: "percMateriais",
    label: "% Materiais Entregues",
    header: "% Mat Entregues",
    main: false,
    align: "right",
    tooltip: "% Materiais Entregues",
  },
  {
    key: "totalFacilidades",
    label: "Total Facilidades",
    header: "Total Facilidades",
    main: false,
    align: "right",
    tooltip: "Total Facilidades",
  },
  {
    key: "percFacilidades",
    label: "% Facilidades Consumidas",
    header: "% Fac Consumidas",
    main: false,
    align: "right",
    tooltip: "% Facilidades Consumidas",
  },
  { key: "detalhamento", label: "Detalhamento", header: "", main: true },
];

/** Nome base do arquivo exportado quando o serviço não informa um. */
export const OBRAS_EXPORT_FILE_NAME = "obras-visualizacao";

export const OBRAS_OPTIONAL_COLUMNS = OBRAS_COLUMNS.filter((column) => !column.main);

/** Por padrão toda coluna do protótipo está visível. */
export const OBRAS_DEFAULT_OPTIONAL_COLUMNS = OBRAS_OPTIONAL_COLUMNS.map(
  (column) => column.key as string,
);

/** As 16 colunas da HU, na ordem da tela, para o `useTableSettings` do DS (definidas fora do render). */
export const OBRAS_ALL_COLUMNS: readonly ObrasColumnKey[] = OBRAS_COLUMNS.map(
  (column) => column.key,
);

/** Colunas principais (definidas na HU): sempre visíveis, travadas no dialog. */
export const OBRAS_MAIN_COLUMNS: readonly ObrasColumnKey[] = OBRAS_COLUMNS.filter(
  (column) => column.main,
).map((column) => column.key);

/** Rótulos do dialog "Configuração da Tabela", na ordem das colunas. */
export const OBRAS_COLUMN_LABELS = Object.fromEntries(
  OBRAS_COLUMNS.map((column) => [column.key, column.label]),
) as Record<ObrasColumnKey, string>;

export const OBRAS_ROWS_PER_PAGE_LABEL = "Obras por página";

export const SAVED_FILTERS_SCREEN = "obras";
export const MOBILE_QUERY = "(max-width: 768px)"; // `$ds-breakpoint-md`: o mesmo corte do Kanban e da barra do DS

/** Chave da coluna de período do painel (mobile); no conjunto salvo ele vira dataInicio/dataFim. */
export const PERIOD_COLUMN = "periodo";
